import { useCallback, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { validateFormValues, valuesToInput } from '../../utils/productionForm';
import type { ProductionFormErrors, ProductionFormValues, ProductionInput } from '../../types/production';

type SubmitState = 'idle' | 'saving' | 'error';

const FIELD_ORDER: (keyof ProductionFormValues)[] = [
  'title',
  'synopsis',
  'videoUrl',
  'posterUrl',
  'status',
  'releaseDate',
  'category',
  'year',
  'durationMinutes',
  'collection',
];

export function useProductionForm(
  initialValues: ProductionFormValues,
  onSubmit: (input: ProductionInput) => Promise<void>,
) {
  const [values, setValues] = useState<ProductionFormValues>(initialValues);
  const [touched, setTouched] = useState<Partial<Record<keyof ProductionFormValues, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const allErrors = useMemo(() => validateFormValues(values), [values]);

  const errors = useMemo(() => {
    const visible: ProductionFormErrors = {};
    (Object.keys(allErrors) as (keyof ProductionFormValues)[]).forEach((key) => {
      if (submitted || touched[key]) visible[key] = allErrors[key];
    });
    return visible;
  }, [allErrors, submitted, touched]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [values, initialValues],
  );

  const setField = useCallback(
    <K extends keyof ProductionFormValues>(key: K, value: ProductionFormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      if (submitState === 'error') setSubmitState('idle');
    },
    [submitState],
  );

  const markTouched = useCallback((key: keyof ProductionFormValues) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setSubmitted(true);
      const firstInvalid = FIELD_ORDER.find((key) => allErrors[key]);
      if (firstInvalid) {
        document.getElementById(`field-${firstInvalid}`)?.focus();
        return;
      }
      setSubmitState('saving');
      setSubmitError(null);
      try {
        await onSubmit(valuesToInput(values));
        setSubmitState('idle');
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'No se pudieron guardar los cambios.');
        setSubmitState('error');
      }
    },
    [allErrors, onSubmit, values],
  );

  return {
    values,
    errors,
    isDirty,
    isSaving: submitState === 'saving',
    submitError: submitState === 'error' ? submitError : null,
    setField,
    markTouched,
    handleSubmit,
  };
}
